/**
 * Offline Save Queue
 * Queues failed saves for retry when connection is restored
 */

import { openDB } from 'idb';

const DB_NAME = 'grapesjs-save-queue';
const STORE_NAME = 'pending-saves';
const DB_VERSION = 1;

/**
 * Initialize IndexedDB
 */
async function initDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        store.createIndex('timestamp', 'timestamp');
        store.createIndex('status', 'status');
      }
    },
  });
}

/**
 * Add save to queue
 * @param {Object} saveData - Data to save
 * @returns {Promise<number>} - Queue item ID
 */
export async function addToQueue(saveData) {
  try {
    const db = await initDB();
    const id = await db.add(STORE_NAME, {
      data: saveData,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      error: null
    });
    
    console.log('📥 Added to save queue:', id);
    return id;
  } catch (error) {
    console.error('❌ Failed to add to queue:', error);
    throw error;
  }
}

/**
 * Get all pending saves
 * @returns {Promise<Array>} - Pending saves
 */
export async function getPendingSaves() {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('status');
    
    return await index.getAll('pending');
  } catch (error) {
    console.error('❌ Failed to get pending saves:', error);
    return [];
  }
}

/**
 * Mark save as completed
 * @param {number} id - Queue item ID
 */
export async function markAsCompleted(id) {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const item = await store.get(id);
    if (item) {
      item.status = 'completed';
      item.completedAt = Date.now();
      await store.put(item);
    }
    
    await tx.done;
    console.log('✅ Marked as completed:', id);
  } catch (error) {
    console.error('❌ Failed to mark as completed:', error);
  }
}

/**
 * Mark save as failed
 * @param {number} id - Queue item ID
 * @param {string} error - Error message
 */
export async function markAsFailed(id, error) {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const item = await store.get(id);
    if (item) {
      item.status = 'failed';
      item.error = error;
      item.retryCount = (item.retryCount || 0) + 1;
      item.lastRetry = Date.now();
      await store.put(item);
    }
    
    await tx.done;
    console.log('❌ Marked as failed:', id, error);
  } catch (error) {
    console.error('❌ Failed to mark as failed:', error);
  }
}

/**
 * Remove save from queue
 * @param {number} id - Queue item ID
 */
export async function removeFromQueue(id) {
  try {
    const db = await initDB();
    await db.delete(STORE_NAME, id);
    console.log('🗑️ Removed from queue:', id);
  } catch (error) {
    console.error('❌ Failed to remove from queue:', error);
  }
}

/**
 * Clear all completed saves
 */
export async function clearCompleted() {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('status');
    
    const completed = await index.getAllKeys('completed');
    for (const key of completed) {
      await store.delete(key);
    }
    
    await tx.done;
    console.log('🧹 Cleared completed saves:', completed.length);
  } catch (error) {
    console.error('❌ Failed to clear completed:', error);
  }
}

/**
 * Get queue statistics
 * @returns {Promise<Object>} - Queue stats
 */
export async function getQueueStats() {
  try {
    const db = await initDB();
    const all = await db.getAll(STORE_NAME);
    
    const stats = {
      total: all.length,
      pending: all.filter(item => item.status === 'pending').length,
      completed: all.filter(item => item.status === 'completed').length,
      failed: all.filter(item => item.status === 'failed').length,
      oldestPending: null
    };
    
    const pending = all.filter(item => item.status === 'pending');
    if (pending.length > 0) {
      stats.oldestPending = Math.min(...pending.map(item => item.timestamp));
    }
    
    return stats;
  } catch (error) {
    console.error('❌ Failed to get queue stats:', error);
    return { total: 0, pending: 0, completed: 0, failed: 0 };
  }
}

/**
 * Process queue - attempt to save all pending items
 * @param {Function} saveFn - Function to save data
 * @returns {Promise<Object>} - Processing results
 */
export async function processQueue(saveFn) {
  const pending = await getPendingSaves();
  
  if (pending.length === 0) {
    console.log('📭 No pending saves in queue');
    return { processed: 0, successful: 0, failed: 0 };
  }
  
  console.log(`🔄 Processing ${pending.length} pending saves...`);
  
  let successful = 0;
  let failed = 0;
  
  for (const item of pending) {
    try {
      await saveFn(item.data);
      await markAsCompleted(item.id);
      successful++;
    } catch (error) {
      await markAsFailed(item.id, error.message);
      failed++;
    }
  }
  
  console.log(`✅ Queue processed: ${successful} successful, ${failed} failed`);
  
  return {
    processed: pending.length,
    successful,
    failed
  };
}

/**
 * Auto-process queue when online
 * @param {Function} saveFn - Function to save data
 */
export function setupAutoProcess(saveFn) {
  // Process queue when coming online
  window.addEventListener('online', async () => {
    console.log('🌐 Connection restored, processing queue...');
    await processQueue(saveFn);
  });
  
  // Check queue on page load
  if (navigator.onLine) {
    setTimeout(async () => {
      const stats = await getQueueStats();
      if (stats.pending > 0) {
        console.log(`📥 Found ${stats.pending} pending saves, processing...`);
        await processQueue(saveFn);
      }
    }, 2000);
  }
}
